import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { TaskFilters, TaskTabs, TaskGrid } from "./TaskBundle";
import { AgentFilters, AgentTabs, AgentGrid } from "./AgentBundle";
import MapPanel from "./MapPanel";
import AssignModal from "../AssignModal";
import TaskDetailsModal from "./TaskDetailsModal";
import SendNotificationModal from "../../Driver/SendNotificationModal";
import { CheckCircle, XCircle } from "lucide-react";
import { Toaster } from "sonner";
import AddTaskModal from "../AddTaskModal";
import DriverDetailsModal from "../../Driver/DriverDetailsModal";
import EditDriverModal from "../../Driver/EditDriverModal";
import DriverConfigModal from "../../Driver/DriverConfigModal";

export default function MapDashboard({
  tasks,
  agents,
  teams,
  selectedTeam,
  setSelectedTeam,
  loading,
  isLoading,
  fromDate,
  toDate,
  shops,
  setFromDate,
  setToDate,

  // old/optional props
  FetchTaskCall,
  FetchDriverCall,
  fetchDrivers,
  sendNotification,

  // modal/add task
  isAddTaskOpen,
  setIsAddTaskOpen,

  editDriverApi,
updateDriverDutyApi,
toggleDutyStatus,
fetchDriverConfigByCode,
fetchSingleDriverConfig,
saveSingleDriverConfig,
deleteDriverConfig,

  // hook based APIs
  fetchTasks,
  fetchTaskLogs,
  handleChangeStatus,
  SyncWithAnlook,
  AssignTaskToDriver,
  handleToggle,
  isChecked,
  baseurl,
  token,
  setIsUnauthorised,
  fetchCustomerInstructions,
  instructionTaskIds,

  // saved/latest dashboard logic
  dashboardTabCounts,
  dashboardTabsLoading,
  selectedDashboardTaskTab,
  setSelectedDashboardTaskTab,
  taskPageInfo,
  loadMoreDashboardTasks,
  taskLoadingMore,
}) {
  // ================= UI STATE =================

const [editDriverModalOpen, setEditDriverModalOpen] = useState(false);
const [editDriver, setEditDriver] = useState(null);

const [configModalOpen, setConfigModalOpen] = useState(false);
const [configDriverCode, setConfigDriverCode] = useState("");

  const selectedTaskTab = selectedDashboardTaskTab;
  const setSelectedTaskTab = setSelectedDashboardTaskTab;

 const selectedStatus = "All Status";
const setSelectedStatus = () => {};
  const [taskSearchQuery, setTaskSearchQuery] = useState("");
  const [agentSearchQuery, setAgentSearchQuery] = useState("");

  // task-side shop filter
  const [selectedShop1, setSelectedShop1] = useState("");

  // agent-side shop filter
  const [selectedShop, setSelectedShop] = useState("");

  const [selectedAgentTab, setSelectedAgentTab] = useState("all drivers");

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [taskToAssign, setTaskToAssign] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState("");

  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [notificationAgent, setNotificationAgent] = useState(null);

  const [isMapZoomed, setIsMapZoomed] = useState(false);

  const [instructionModalOpen, setInstructionModalOpen] = useState(false);
  const [taskInstructions, setTaskInstructions] = useState([]);
  const [loadingInst, setLoadingInst] = useState(false);

  // date handling
  const [tempFromDate, setTempFromDate] = useState(fromDate);
  const [tempToDate, setTempToDate] = useState(toDate);
  const [appliedFromDate, setAppliedFromDate] = useState(fromDate);
  const [appliedToDate, setAppliedToDate] = useState(toDate);

  // logs modal state
  const [assignmentLogs, setAssignmentLogs] = useState([]);
  const [statusChangeLogs, setStatusChangeLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const [selectedDriver, setSelectedDriver] = useState(null);
const [showDriverDetailsModal, setShowDriverDetailsModal] = useState(false);

  // tab switching shimmer
  const [isTabSwitching, setIsTabSwitching] = useState(false);

  // assign modal
  const [assignTab, setAssignTab] = useState("team");
  const [selectedAssignTeam, setSelectedAssignTeam] = useState("");

  // ================= DATE SYNC =================

  useEffect(() => {
    if (fromDate) {
      setTempFromDate(fromDate);
      setAppliedFromDate(fromDate);
    }

    if (toDate) {
      setTempToDate(toDate);
      setAppliedToDate(toDate);
    }
  }, [fromDate, toDate]);

  // ================= HELPERS =================

  const isDateInRange = useCallback((taskDate, fromDate, toDate) => {
    if (!taskDate) return true;
    if (!fromDate && !toDate) return true;

    const taskDateObj = new Date(taskDate);
    const fromDateObj = fromDate ? new Date(fromDate) : null;
    const toDateObj = toDate ? new Date(toDate) : null;

    if (fromDateObj && taskDateObj < fromDateObj) return false;
    if (toDateObj && taskDateObj > toDateObj) return false;

    return true;
  }, []);

  const normalizeStatus = (s = "") =>
    s?.toLowerCase().replace(/\s+/g, " ").trim();

  // ================= TASK FILTERING =================

  const baseFilteredTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];

    let filtered = [...tasks];

    // ---- SHOP FILTER ----
    if (selectedShop1) {
      filtered = filtered.filter((task) => {
        const taskShopCode =
          task?.shopcode || task?.shop_code || task?.restaurant_code;

        return taskShopCode?.toString() === selectedShop1.toString();
      });
    }

    // ---- DATE FILTER ----
    if (appliedFromDate || appliedToDate) {
      filtered = filtered.filter((task) =>
        isDateInRange(
          task?.created_at || task?.createdAt || task?.date,
          appliedFromDate,
          appliedToDate
        )
      );
    }

    return filtered;
  }, [tasks, selectedShop1, appliedFromDate, appliedToDate, isDateInRange]);

  const statusFilteredTasks = useMemo(() => {
    let filtered = [...baseFilteredTasks];

    if (selectedStatus !== "All Status") {
      const filter = normalizeStatus(selectedStatus);

      const statusMap = {
        unassigned: ["unassigned"],
        assigned: ["assigned", "started", "inprogress"],
        acknowledged: ["acknowledged"],
        arrived: ["arrived"],
        "food is ready": ["food is ready"],
        "picked up": ["picked up"],
        completed: ["successful"],
        cancelled: ["cancelled", "failed", "declined"],
      };

      const allowedStatuses = statusMap[filter];

      if (allowedStatuses) {
        filtered = filtered.filter((task) =>
          allowedStatuses.includes(normalizeStatus(task?.status))
        );
      }
    }

    return filtered;
  }, [baseFilteredTasks, selectedStatus]);

  const handleModalDutyToggle = async (driverObj, isCurrentlyOn) => {
  if (!driverObj || typeof toggleDutyStatus !== "function") return;

  await toggleDutyStatus(driverObj, isCurrentlyOn);

  const updatedDrivers = await fetchDrivers?.(true);

  if (Array.isArray(updatedDrivers)) {
    const updatedDriver = updatedDrivers.find(
      (d) =>
        d.driverId === driverObj.driverId ||
        d.driverId === driverObj.driver_code ||
        d.driver_code === driverObj.driverId
    );

    if (updatedDriver) {
      setSelectedDriver(updatedDriver);
    } else {
      setSelectedDriver((prev) =>
        prev
          ? {
              ...prev,
              dutyStatus: isCurrentlyOn ? "offline" : "active",
            }
          : prev
      );
    }
  } else {
    setSelectedDriver((prev) =>
      prev
        ? {
            ...prev,
            dutyStatus: isCurrentlyOn ? "offline" : "active",
          }
        : prev
    );
  }
};

  const filteredTasks = useMemo(() => {
    let result = [...statusFilteredTasks];

    // ---- TAB FILTER ----
    if (selectedTaskTab === "unassigned") {
      result = result.filter(
        (task) => task?.status?.toLowerCase() === "unassigned"
      );
    }

    if (selectedTaskTab === "completed") {
      result = result.filter(
        (task) => task?.status?.toLowerCase() === "successful"
      );
    }

    if (selectedTaskTab === "assigned") {
      result = result.filter((task) =>
        ["assigned", "started", "inprogress"].includes(
          task?.status?.toLowerCase()
        )
      );
    }

    if (selectedTaskTab === "cancelled") {
      result = result.filter((task) =>
        ["cancelled", "failed", "declined"].includes(
          task?.status?.toLowerCase()
        )
      );
    }

    // ---- SEARCH FILTER ----
    if (taskSearchQuery.trim()) {
      const q = taskSearchQuery.toLowerCase();

      result = result.filter((task) =>
        JSON.stringify(task).toLowerCase().includes(q)
      );
    }

    return result;
  }, [statusFilteredTasks, selectedTaskTab, taskSearchQuery]);

  // ================= AGENT FILTERING =================

  const filteredAgents = useMemo(() => {
    let list = agents || [];

    if (selectedTeam && selectedTeam !== "All Team") {
      const team = teams.find((t) => t.team_name === selectedTeam);
      const ids = team?.driverlist?.map((d) => d.id?.toString()) || [];

      list = list.filter(
        (agent) =>
          ids.includes(agent?.id?.toString()) ||
          ids.includes(agent?.driverId?.toString())
      );
    }

    if (selectedShop) {
      const teamsWithShop = teams.filter((team) =>
        team.shopList?.some(
          (shop) => shop.shop_code?.toString() === selectedShop.toString()
        )
      );

      const validIds = teamsWithShop.flatMap(
        (team) => team.driverlist?.map((driver) => driver.id?.toString()) || []
      );

      list = list.filter(
        (agent) =>
          validIds.includes(agent?.id?.toString()) ||
          validIds.includes(agent?.driverId?.toString())
      );
    }

    if (selectedAgentTab !== "all drivers") {
      list = list.filter(
        (agent) => agent?.dutyStatus?.toLowerCase() === selectedAgentTab
      );
    }

    if (agentSearchQuery.trim()) {
      const q = agentSearchQuery.toLowerCase();

      list = list.filter(
        (agent) =>
          agent?.fullName?.toLowerCase().includes(q) ||
          agent?.mobile?.includes(q)
      );
    }

    return list;
  }, [
    agents,
    teams,
    selectedTeam,
    selectedShop,
    selectedAgentTab,
    agentSearchQuery,
  ]);

  // ================= DRIVERS FOR ASSIGN =================

  const teamDrivers = useMemo(() => {
    if (!selectedTeam || selectedTeam === "All Team") return [];

    const teamData = teams.find((team) => team.team_name === selectedTeam);
    const ids = teamData?.driverlist?.map((driver) => driver.id?.toString()) || [];

    return (agents || []).filter(
      (agent) =>
        ids.includes(agent?.id?.toString()) ||
        ids.includes(agent?.driverId?.toString())
    );
  }, [selectedTeam, teams, agents]);

  const allTeamDrivers = useMemo(() => {
    if (!selectedAssignTeam) return [];

    const teamData = teams.find((team) => team.team_name === selectedAssignTeam);
    const ids = teamData?.driverlist?.map((driver) => driver.id?.toString()) || [];

    return (agents || []).filter(
      (agent) =>
        ids.includes(agent?.id?.toString()) ||
        ids.includes(agent?.driverId?.toString())
    );
  }, [selectedAssignTeam, teams, agents]);

  // ================= ACTIONS =================

  const handleAssignTask = async () => {
  if (!selectedAgentId || !taskToAssign) return;

  const taskId =
    taskToAssign?.pk_task_id ||
    taskToAssign?.taskId ||
    taskToAssign?.task_id ||
    taskToAssign?.id;

  const oldDriverId =
    taskToAssign?.driver_id ||
    taskToAssign?.driverId ||
    taskToAssign?.driver_id_fk ||
    "0";

  if (!taskId) return;

  try {
    // Step 1: first make task unassigned
    const unassignSuccess = await handleChangeStatus(
      taskId,
      "unassigned",
      oldDriverId,
    );

    if (!unassignSuccess) return;

    // Step 2: then assign selected driver
    const assignSuccess = await AssignTaskToDriver(taskId, selectedAgentId);

    if (assignSuccess) {
      setShowAssignModal(false);
      setSelectedAgentId("");
      setTaskToAssign(null);

      setAssignmentLogs([]);
      setStatusChangeLogs([]);
      setShowLogs(false);

      if (typeof fetchTasks === "function") {
        await fetchTasks(fromDate, toDate, () => {});
      }
    }
  } catch (error) {
    console.error("Assign task flow error:", error);
  }
};

 const handleAssignContext = (task) => {
  setTaskToAssign(task);
  setSelectedAgentId("");

  const taskShopCode =
    task?.shopcode ||
    task?.shop_code ||
    task?.restaurant_code ||
    task?.shopCode ||
    task?.shop;

  const matchedTeam = teams?.find((t) =>
    t.shopList?.some(
      (s) => s.shop_code?.toString() === taskShopCode?.toString(),
    ),
  );

  if (matchedTeam) {
    setSelectedAssignTeam(matchedTeam.team_name);
    setAssignTab("team");
  } else {
    setSelectedAssignTeam("");
    setAssignTab("all");
  }

  setShowAssignModal(true);
};

  const handleTaskTabChange = (tab) => {
    setIsTabSwitching(true);
    setSelectedTaskTab(tab);

    const map = {
      all: "All Status",
      unassigned: "Unassigned",
      assigned: "Assigned",
      acknowledged: "Acknowledged",
      arrived: "Arrived",
      "food is ready": "Food is Ready",
      "picked up": "Picked up",
      completed: "Completed",
      cancelled: "Cancelled",
    };

    setSelectedStatus(map[tab] || tab);
  };

  // ================= EFFECTS =================

  useEffect(() => {
    if (!selectedTeam) setSelectedTeam("All Team");
  }, [selectedTeam, setSelectedTeam]);

  useEffect(() => {
    setSelectedShop("");
  }, [selectedTeam]);

  useEffect(() => {
    const mapStatusToTab = {
      "All Status": "all",
      Unassigned: "unassigned",
      Assigned: "assigned",
      Acknowledged: "acknowledged",
      Arrived: "arrived",
      "Food is Ready": "food is ready",
      "Picked up": "picked up",
      Completed: "completed",
      Cancelled: "cancelled",
    };

    if (mapStatusToTab[selectedStatus]) {
      setSelectedTaskTab(mapStatusToTab[selectedStatus]);
    }
  }, [selectedStatus, setSelectedTaskTab]);

  useEffect(() => {
    if (!isLoading && isTabSwitching) {
      setIsTabSwitching(false);
    }
  }, [isLoading, isTabSwitching]);

  return (
    <div className="flex w-full h-screen overflow-hidden bg-white gap-4">
      {/* LEFT PANEL */}
      <div className="flex-1 min-w-[900px] h-full flex flex-col overflow-hidden p-2">
        <div className="sticky top-0 z-30 bg-white overflow-x-auto">
          <div className="text-black flex justify-between font-semibold text-2xl">
            <span>Tasks</span>

            <div
              role="tablist"
              aria-label="Switch Auto/Manual"
              className={`relative inline-grid grid-cols-2 w-[88px] rounded-full overflow-x-auto bg-white border border-gray-200 p-1 ${
                loading ? "opacity-60 pointer-events-none" : ""
              }`}
            >
              {/* Sliding pill */}
              <span
                aria-hidden="true"
                className="absolute top-1 bottom-1 left-1 rounded-full bg-white shadow transition-transform duration-300 ease-in-out pointer-events-none"
                style={{
                  width: "calc(50% - 0.25rem)",
                  transform: isChecked ? "translateX(100%)" : "translateX(0%)",
                }}
              />

              {/* Manual */}
              <button
                type="button"
                role="tab"
                aria-selected={!isChecked}
                className="relative z-10 flex items-center justify-center px-1 py-1 rounded-full"
                onClick={() => handleToggle(false)}
              >
                <XCircle
                  className={`h-4 w-4 ${
                    !isChecked ? "text-red-500" : "text-gray-500"
                  }`}
                />
              </button>

              {/* Auto */}
              <button
                type="button"
                role="tab"
                aria-selected={isChecked}
                className="relative z-10 flex items-center justify-center px-1 py-1 rounded-full"
                onClick={() => handleToggle(true)}
              >
                <CheckCircle
                  className={`h-4 w-4 ${
                    isChecked ? "text-green-600" : "text-gray-500"
                  }`}
                />
              </button>
            </div>
          </div>

          <Toaster position="top-right" />

          <div className="sticky top-0 z-20 bg-white overflow-visible">
            <div className="relative z-[600] overflow-visible">
              <TaskFilters
                taskSearchQuery={taskSearchQuery}
                setTaskSearchQuery={setTaskSearchQuery}
                shops={shops}
                loading={loading}
                selectedShop1={selectedShop1}
                setSelectedShop1={setSelectedShop1}
                tempFromDate={tempFromDate}
                tempToDate={tempToDate}
                setTempFromDate={setTempFromDate}
                setTempToDate={setTempToDate}
                onApply={() => {
                  setAppliedFromDate(tempFromDate);
                  setAppliedToDate(tempToDate);
                  setFromDate(tempFromDate);
                  setToDate(tempToDate);
                }}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
              />
            </div>

            <div className="relative z-10">
              <TaskTabs
                selectedTaskTab={selectedTaskTab}
                setSelectedTaskTab={handleTaskTabChange}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                dashboardTabsLoading={dashboardTabsLoading}
                getTaskTabCount={(tab) => dashboardTabCounts?.[tab] || 0}
              />
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <TaskGrid
            isLoading={isLoading}
            isStatusChanging={false}
            isTabSwitching={isTabSwitching}
            tasks={filteredTasks}
            onAssignClick={(task) => {
              setTaskToAssign(task);
              setShowAssignModal(true);
            }}
            onDetailsClick={(task) => {
              setSelectedTask(task);
              setShowDetailsModal(true);

              setAssignmentLogs([]);
              setStatusChangeLogs([]);
              setShowLogs(false);
            }}
            SyncWithAnlook={SyncWithAnlook}
            instructionTaskIds={instructionTaskIds}
            onInstructionClick={async (taskId) => {
              setLoadingInst(true);

              const allData = await fetchCustomerInstructions();

              const onlyThisTask = (allData || []).filter(
                (item) => String(item.task_id) === String(taskId),
              );

              setTaskInstructions(onlyThisTask);
              setInstructionModalOpen(true);
              setLoadingInst(false);
            }}
            getShopName={(code) =>
              shops?.find(
                (shop) =>
                  shop.shop_code === code ||
                  shop.code === code ||
                  shop.restaurant_code === code,
              )?.shop_name
            }
            onLoadMore={loadMoreDashboardTasks}
            hasMore={Boolean(taskPageInfo?.nextToken)}
            isLoadingMore={taskLoadingMore}
          />
        </div>
      </div>

      {/* INSTRUCTION MODAL */}
      {instructionModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded-xl w-[520px] shadow-lg">
            <h2 className="text-lg font-bold mb-3">Customer Instructions</h2>

            {loadingInst ? (
              <p>Loading...</p>
            ) : Array.isArray(taskInstructions) &&
              taskInstructions.length > 0 ? (
              taskInstructions.map((item) => (
                <div
                  key={item.pk_customer_delivery_instruction_id}
                  className="mb-3 border-b pb-3"
                >
                  <p className="font-semibold text-base">
                    {item.instuction_title || "Instruction"}
                  </p>

                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Default note:</strong>{" "}
                    {item.instuction_description || "No description"}
                  </p>

                  {item.customer_instruction_messege && (
                    <p className="text-sm text-orange-700 mt-1">
                      <strong>Customer message:</strong>{" "}
                      {item.customer_instruction_messege}
                    </p>
                  )}

                  <p className="text-xs text-gray-600 mt-1">
                    Shop: {item.shop_name || "N/A"} • Task: {item.task_id}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No instructions for this task.</p>
            )}

            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setInstructionModalOpen(false);
                  setTaskInstructions([]);
                }}
                className="mt-3 bg-red-500 text-white px-4 py-2 rounded flex justify-end"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RIGHT PANEL */}
      <div className="w-[450px] bg-white flex flex-col h-[calc(100vh-80px)]">
        <AgentFilters
          teams={teams}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          selectedShop={selectedShop}
          setSelectedShop={setSelectedShop}
          loading={loading}
          agentSearchQuery={agentSearchQuery}
          setAgentSearchQuery={setAgentSearchQuery}
          fetchDrivers={fetchDrivers}
          FetchDriverCall={FetchDriverCall}
        />

        <AgentTabs
          selectedAgentTab={selectedAgentTab}
          setSelectedAgentTab={setSelectedAgentTab}
          getAgentTabCount={(tab) => {
            const baseList = agents || [];

            if (tab === "all drivers") return baseList.length;

            return baseList.filter(
              (agent) => agent?.dutyStatus?.toLowerCase() === tab,
            ).length;
          }}
        />

        <AgentGrid
          agents={filteredAgents}
          onNotify={(agent) => {
            setNotificationAgent(agent);
            setNotificationModalOpen(true);
          }}
          onDriverClick={(agent) => {
            setSelectedDriver(agent);
            setShowDriverDetailsModal(true);
          }}
        />

        {!isAddTaskOpen && (
          <MapPanel
            tasks={filteredTasks}
            drivers={filteredAgents}
            teams={teams}
            isMapZoomed={isMapZoomed}
            setIsMapZoomed={setIsMapZoomed}
          />
        )}
      </div>

      {/* ASSIGN MODAL */}
      <AssignModal
        show={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        teams={teams}
        assignTab={assignTab}
        setAssignTab={setAssignTab}
        selectedAssignTeam={selectedAssignTeam}
        setSelectedAssignTeam={setSelectedAssignTeam}
        teamDrivers={teamDrivers}
        allTeamDrivers={allTeamDrivers}
        selectedAgentId={selectedAgentId}
        setSelectedAgentId={setSelectedAgentId}
        handleAssignTask={handleAssignTask}
      />

      {/* TASK DETAILS MODAL */}
      <TaskDetailsModal
        show={showDetailsModal}
        task={selectedTask}
        isLoading={isModalLoading}
        onClose={() => setShowDetailsModal(false)}
        assignmentLogs={assignmentLogs}
        statusChangeLogs={statusChangeLogs}
        showLogs={showLogs}
        setShowLogs={setShowLogs}
        onFetchLogs={async (taskId) => {
          const data = await fetchTaskLogs(taskId);

          setAssignmentLogs(data?.assignmentLogs || []);
          setStatusChangeLogs(data?.statusChangeLogs || []);
        }}
        onStatusChange={async (taskId, newStatus) => {
          const success = await handleChangeStatus(
            taskId,
            newStatus,
            selectedTask?.driver_id,
          );

          if (success) {
            setShowDetailsModal(false);
          }
        }}
        onUnassignRequest={(task) => {
          setShowDetailsModal(false);
          handleAssignContext(task);
        }}
      />

      {/* ADD TASK MODAL */}
      {/* <AddTaskModal
        isOpen={isAddTaskOpen}
        onClose={() => setIsAddTaskOpen(false)}
        shops={shops}
      /> */}

      {/* NOTIFICATION MODAL */}
      {notificationModalOpen && (
        <SendNotificationModal
          isOpen={notificationModalOpen}
          onClose={() => setNotificationModalOpen(false)}
          driver={notificationAgent}
          baseurl={baseurl}
          token={token}
          setIsUnauthorised={setIsUnauthorised}
          sendSingleNotificationApi={sendNotification}
        />
      )}

      <DriverDetailsModal
        open={showDriverDetailsModal}
        onOpenChange={setShowDriverDetailsModal}
        driver={selectedDriver}
        fetchDriverConfigByCode={fetchDriverConfigByCode}
        onOpenConfig={(driverCode) => {
          setConfigDriverCode(driverCode);
          setConfigModalOpen(true);
        }}
        onEditDriver={(driverObj) => {
          setShowDriverDetailsModal(false);
          setEditDriver(driverObj);
          setEditDriverModalOpen(true);
        }}
        onToggleDutyStatus={handleModalDutyToggle}
        dutyLoading={loading}
      />

      <EditDriverModal
        isOpen={editDriverModalOpen}
        onClose={() => setEditDriverModalOpen(false)}
        driver={editDriver}
        teams={teams}
        onDriverUpdated={() => {
          if (typeof fetchDrivers === "function") {
            fetchDrivers();
          }
        }}
        editDriverApi={editDriverApi}
        updateDriverDutyApi={updateDriverDutyApi}
      />

      <DriverConfigModal
        open={configModalOpen}
        onOpenChange={setConfigModalOpen}
        driverCode={configDriverCode}
        onSuccess={() => {
          if (typeof fetchDrivers === "function") {
            fetchDrivers();
          }
        }}
        fetchSingleDriverConfig={fetchSingleDriverConfig}
        saveSingleDriverConfig={saveSingleDriverConfig}
        deleteSingleDriverConfig={deleteDriverConfig}
      />
    </div>
  );
}